import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { emit as emitEvent } from "@/lib/events";
import { Users, Upload, Loader2, Search, Trash2, FileText, ClipboardPaste, CheckCircle2, X, ExternalLink, AlertCircle } from "lucide-react";

const money = (v) =>
  v == null || isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Inline panel — sits at the bottom of /pricing-history. Lets contractors
 * upload a CSV or paste a table of real winning federal awards from friends /
 * industry peers. The Estimator uses these as its strongest calibration
 * anchor (see backend `_calibration_for_opportunity`).
 */
export default function PeerWinsPanel({ initialTab = "history" }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("file"); // "file" | "paste"
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [lastImport, setLastImport] = useState(null);
  const fileRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/peer-wins/summary"),
        api.get("/peer-wins", { params: q ? { q } : {} }),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const t = setTimeout(() => loadAll(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const doImport = async ({ file, text } = {}) => {
    setUploading(true);
    setLastImport(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      else if (text && text.trim()) fd.append("text", text);
      else {
        toast.error("Choose a file or paste rows first");
        setUploading(false);
        return;
      }
      const r = await api.post("/peer-wins/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImport(r.data);
      toast.success(`Imported ${r.data.imported} peer wins from ${r.data.source}`);
      emitEvent("peer_wins:changed", { reason: "import", imported: r.data.imported });
      setPasteText("");
      if (fileRef.current) fileRef.current.value = "";
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const onFile = (f) => {
    if (!f) return;
    if (!/\.(csv|tsv|xlsx|xls)$/i.test(f.name)) {
      toast.error("Only .csv, .tsv, .xls, and .xlsx are supported");
      return;
    }
    doImport({ file: f });
  };

  const removeRow = async (id) => {
    try {
      await api.delete(`/peer-wins/${id}`);
      setItems((its) => its.filter((r) => r.item_id !== id));
      setSummary((s) => (s ? { ...s, count: Math.max(0, s.count - 1) } : s));
      emitEvent("peer_wins:changed", { reason: "delete", item_id: id });
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL peer wins for this workspace? This cannot be undone.")) return;
    try {
      const r = await api.delete("/peer-wins", { data: { confirm: true } });
      toast.success(`Cleared ${r.data.deleted} peer wins`);
      emitEvent("peer_wins:changed", { reason: "clear" });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear");
    }
  };

  return (
    <section className="mt-12 pt-10 border-t border-slate-200" data-testid="peer-wins-panel">
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-sky-600">// PEER WINS CALIBRATION</div>
          <h2 className="font-display font-black text-2xl md:text-3xl text-slate-900">Real winning awards from your peers</h2>
        </div>
      </div>
      <p className="text-slate-600 max-w-2xl mb-6">
        Upload real winning federal awards from friends or industry peers. The Estimator uses this corpus as its
        strongest calibration anchor — matched by scope, trade, and state — so your bids get sized against actual
        dollars, not just industry averages.
      </p>

      {/* Tabs — paste vs file */}
      <div className="flex gap-1 mb-4">
        <button
          onClick={() => setTab("file")}
          className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 ${
            tab === "file"
              ? "border-sky-500 text-sky-700 bg-white"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="peer-tab-file"
        >
          <Upload className="w-3.5 h-3.5 inline mr-1.5" />
          UPLOAD FILE
        </button>
        <button
          onClick={() => setTab("paste")}
          className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 ${
            tab === "paste"
              ? "border-sky-500 text-sky-700 bg-white"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="peer-tab-paste"
        >
          <ClipboardPaste className="w-3.5 h-3.5 inline mr-1.5" />
          PASTE FROM SPREADSHEET
        </button>
      </div>

      {tab === "file" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer?.files?.[0]); }}
          className={`rounded-2xl border-2 border-dashed p-8 mb-6 text-center transition-all ${
            dragging ? "border-sky-500 bg-sky-50" : "border-slate-300 bg-white hover:border-sky-400"
          }`}
          data-testid="peer-dropzone"
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-sky-600" : "text-slate-400"}`} />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">
            {uploading ? "Uploading…" : "Drop a peer-wins CSV here"}
          </div>
          <div className="text-sm text-slate-500 mb-4">CSV, TSV, XLSX, or XLS · up to 5 MB · 1,000 rows</div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.tsv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="peer-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="peer-choose-file"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "UPLOADING" : "CHOOSE FILE"}
          </button>
          <div className="text-xs text-slate-400 mt-4">
            Expected columns: <code className="font-mono">Award ID</code>, <code className="font-mono">Award Amount</code>, <code className="font-mono">Agency</code>, <code className="font-mono">State</code>, <code className="font-mono">Description</code>, <code className="font-mono">Known SF</code>, <code className="font-mono">Known $/SF</code>, <code className="font-mono">Relevance</code>. Headers are fuzzy-matched.
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder={
              "Award ID,Award Amount,Award Date,Agency,State,Description,Known SF,Known $/SF\n" +
              "1240LU24C0066,276790,2024-10-01,Forest Service,ID,KOOTENAI NURSERY ROOFING REPLACEMENT,15100-20300 SF,$13.63-$18.33/SF"
            }
            className="w-full font-mono text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 focus:border-sky-400 outline-none"
            data-testid="peer-paste-textarea"
          />
          <div className="flex items-center justify-end mt-3">
            <button
              onClick={() => doImport({ text: pasteText })}
              disabled={uploading || !pasteText.trim()}
              className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="peer-paste-import"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "IMPORTING" : "IMPORT ROWS"}
            </button>
          </div>
        </div>
      )}

      {lastImport && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3" data-testid="peer-last-import">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-900">
              Imported {lastImport.imported} peer wins from {lastImport.source}
              {lastImport.skipped > 0 && <span className="text-emerald-700 font-normal"> · {lastImport.skipped} skipped</span>}
            </div>
            <div className="text-xs text-emerald-700 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {Object.entries(lastImport.column_mapping || {}).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} className="font-mono">
                  {k}: <span className="font-bold">{v}</span>
                </span>
              ))}
            </div>
          </div>
          <button onClick={() => setLastImport(null)} className="text-emerald-600 hover:text-emerald-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Stat label="PEER WINS" value={summary?.count?.toLocaleString() ?? "—"} testid="peer-summary-count" />
        <Stat label="AGENCIES" value={summary?.agencies?.length ?? "—"} />
        <Stat label="STATES" value={summary?.states?.length ?? "—"} />
        <Stat label="AVG AWARD" value={money(summary?.avg_award)} />
      </div>

      {/* Search + clear */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search description, agency, or keyword"
            className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-sm focus:border-sky-400 outline-none"
            data-testid="peer-search"
          />
        </div>
        {(summary?.count ?? 0) > 0 && (
          <button
            onClick={clearAll}
            className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-rose-600 border border-slate-200 hover:border-rose-300 rounded-full px-4 py-2.5"
            data-testid="peer-clear-all"
          >
            CLEAR ALL
          </button>
        )}
      </div>

      {/* List */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="peer-empty">
            <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            {q ? `No peer wins match "${q}"` : "No peer wins uploaded yet. Drop a CSV above to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="peer-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-mono font-bold tracking-widest text-slate-500">
                  <th className="px-4 py-3">AGENCY</th>
                  <th className="px-4 py-3 w-14">ST</th>
                  <th className="px-4 py-3 w-28 text-right">AWARD</th>
                  <th className="px-4 py-3 w-32">$/SF</th>
                  <th className="px-4 py-3">DESCRIPTION</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.item_id} className={i % 2 ? "bg-slate-50/40" : "bg-white"} data-testid={`peer-row-${r.item_id}`}>
                    <td className="px-4 py-3 text-slate-800 truncate max-w-[220px]" title={r.agency}>{r.agency || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.state || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{money(r.award_amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                      {r.known_dollar_per_sf?.median != null
                        ? `$${r.known_dollar_per_sf.min.toFixed(2)}-$${r.known_dollar_per_sf.max.toFixed(2)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[400px]" title={r.description}>
                      {r.description || "—"}
                    </td>
                    <td className="px-4 py-3 flex items-center gap-1">
                      {r.link && (
                        <a href={r.link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-600" title="USAspending">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        onClick={() => removeRow(r.item_id)}
                        className="text-slate-300 hover:text-rose-500 transition-colors"
                        data-testid={`peer-row-${r.item_id}-remove`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-black text-2xl text-slate-900">{value}</div>
    </div>
  );
}
